import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import type { RenderStats } from '../shared/messages';
import { useT } from './i18n';

export interface ViewerProps {
  original: ImageBitmap | null;
  result: ImageBitmap | null;
  stats: RenderStats | null;
  busy: boolean;
  progress: number;
  progressLabel: string;
  exporting: boolean;
  /** invoked by Ctrl/Cmd+E */
  onExportShortcut?: () => void;
  children?: ComponentChildren;
}

/**
 * Discrete zoom levels.
 *
 * Integer ratios above 1:1 keep the pixel grid crisp, which is the whole point
 * of a pixel-art viewer. They are a fixed ladder rather than a rounded
 * continuous zoom: rounding a multiplicative step keeps landing on the level it
 * started from (`1 * 1.21 = 1.21 -> 1`), so zoom would stick at 1x and 2x and
 * could never climb.
 */
const ZOOM_LEVELS = [
  0.1, 0.25, 0.5, 0.75, 1, 2, 3, 4, 5, 6, 8, 10, 12, 16, 20, 24, 32, 40, 48, 64,
];

function nearestLevel(z: number): number {
  let best = 0;
  for (let i = 1; i < ZOOM_LEVELS.length; i++) {
    if (Math.abs(ZOOM_LEVELS[i] - z) < Math.abs(ZOOM_LEVELS[best] - z)) best = i;
  }
  return best;
}

export function Viewer({
  original,
  result,
  stats,
  busy,
  progress,
  progressLabel,
  exporting,
  onExportShortcut,
  children,
}: ViewerProps) {
  const t = useT();
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [level, setLevel] = useState(nearestLevel(1));
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const zoom = ZOOM_LEVELS[level];
  const [split, setSplit] = useState(0.5);
  const [peek, setPeek] = useState(false);
  const [fitPending, setFitPending] = useState(true);
  const dragRef = useRef<{ x: number; y: number; px: number; py: number } | null>(null);

  // ------------------------------------------------------------ sizing
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setSize({ w: el.clientWidth, h: el.clientHeight });
    });
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  const imageW = result?.width ?? 0;
  const imageH = result?.height ?? 0;

  const fitZoom = useMemo(() => {
    if (!imageW || !imageH || !size.w || !size.h) return 1;
    const raw = Math.min((size.w - 32) / imageW, (size.h - 32) / imageH);
    return raw >= 1 ? Math.floor(raw) : raw;
  }, [imageW, imageH, size.w, size.h]);

  useEffect(() => {
    if (fitPending && imageW && size.w) {
      setLevel(nearestLevel(fitZoom));
      setPan({ x: 0, y: 0 });
      setFitPending(false);
    }
  }, [fitPending, fitZoom, imageW, size.w]);

  const fit = useCallback(() => {
    setLevel(nearestLevel(fitZoom));
    setPan({ x: 0, y: 0 });
  }, [fitZoom]);

  // Where the image actually lands inside the viewer. The split handle and the
  // before/after clip both have to be derived from this, not from the viewer
  // rect — the image is centred and scaled, so its edges rarely line up with the
  // viewport's.
  const view = useMemo(() => {
    const dw = imageW * zoom;
    const dh = imageH * zoom;
    return {
      dw,
      dh,
      x: Math.round((size.w - dw) / 2 + pan.x),
      y: Math.round((size.h - dh) / 2 + pan.y),
    };
  }, [imageW, imageH, zoom, pan.x, pan.y, size.w, size.h]);

  // ------------------------------------------------------------ drawing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !size.w || !size.h) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = Math.round(size.w * dpr);
    const H = Math.round(size.h * dpr);
    if (canvas.width !== W || canvas.height !== H) {
      canvas.width = W;
      canvas.height = H;
    }
    const c = canvas.getContext('2d');
    if (!c) return;
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    c.imageSmoothingEnabled = false;
    c.clearRect(0, 0, size.w, size.h);

    if (!result) return;
    const { dw, dh, x, y } = view;

    const showBefore = !!original && (peek || split > 0.001);
    if (!peek) {
      c.drawImage(result, x, y, dw, dh);
    }
    if (showBefore) {
      const edge = peek ? size.w : x + dw * split;
      c.save();
      c.beginPath();
      c.rect(0, 0, edge, size.h);
      c.clip();
      c.drawImage(original!, x, y, dw, dh);
      c.restore();
    }
  }, [original, result, size.w, size.h, view, split, peek]);

  // -------------------------------------------------------- interaction
  const wheelAccum = useRef(0);
  const levelRef = useRef(level);
  levelRef.current = level;

  const onWheel = useCallback(
    (e: WheelEvent) => {
      if (!result) return;
      e.preventDefault();
      // Trackpads emit many small deltas; accumulate so a flick still moves
      // several levels while a mouse notch moves exactly one.
      wheelAccum.current += e.deltaY;
      const steps = Math.trunc(wheelAccum.current / 100);
      if (steps === 0) return;
      wheelAccum.current -= steps * 100;

      const from = levelRef.current;
      const to = Math.min(ZOOM_LEVELS.length - 1, Math.max(0, from - steps));
      if (to === from) return;
      levelRef.current = to;
      setLevel(to);

      // keep the point under the cursor stable
      const rect = wrapRef.current!.getBoundingClientRect();
      const k = ZOOM_LEVELS[to] / ZOOM_LEVELS[from];
      const ox = e.clientX - rect.left - rect.width / 2;
      const oy = e.clientY - rect.top - rect.height / 2;
      setPan((p) => ({ x: ox - (ox - p.x) * k, y: oy - (oy - p.y) * k }));
    },
    [result],
  );

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  const onPointerDown = (e: PointerEvent) => {
    // Only the image itself starts a pan. Capturing the pointer from an overlay
    // (the drop zone, the split handle) would swallow its clicks entirely.
    if (!(e.target instanceof HTMLCanvasElement)) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
  };
  const onPointerMove = (e: PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    setPan({ x: d.px + (e.clientX - d.x), y: d.py + (e.clientY - d.y) });
  };
  const onPointerUp = (e: PointerEvent) => {
    dragRef.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
  };

  const onSplitDown = (e: PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const { x: ix, dw } = view;
    const move = (ev: PointerEvent) => {
      if (dw <= 0) return;
      const rect = wrapRef.current!.getBoundingClientRect();
      // the split is a fraction of the *image*, not of the viewer
      setSplit(Math.min(1, Math.max(0, (ev.clientX - rect.left - ix) / dw)));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // -------------------------------------------------------- shortcuts
  useEffect(() => {
    const isTyping = (el: EventTarget | null) =>
      el instanceof HTMLElement &&
      (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA');
    const down = (e: KeyboardEvent) => {
      if (isTyping(e.target)) return;
      if (e.code === 'Space') {
        e.preventDefault();
        setPeek(true);
      } else if (e.key === 'f' || e.key === 'F') {
        fit();
      } else if (e.key === '1') {
        setLevel(nearestLevel(1));
        setPan({ x: 0, y: 0 });
      } else if (e.key === '+' || e.key === '=') {
        setLevel((i) => Math.min(ZOOM_LEVELS.length - 1, i + 1));
      } else if (e.key === '-' || e.key === '_') {
        setLevel((i) => Math.max(0, i - 1));
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'e' || e.key === 'E')) {
        e.preventDefault();
        onExportShortcut?.();
      }
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === 'Space') setPeek(false);
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, [fit, onExportShortcut]);

  return (
    <div
      class="viewer"
      ref={wrapRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onDblClick={fit}
    >
      <canvas ref={canvasRef} />
      {progress > 0 && progress < 1 && (
        <div class="progress-track">
          <i style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
      )}
      {result && (
        <div
          class="split-handle"
          style={{ left: `${view.x + view.dw * split}px` }}
          onPointerDown={onSplitDown}
          title={t('viewer.splitHint')}
        >
          <div class="bar" />
          <div class="knob">⇔</div>
        </div>
      )}
      {(busy || exporting) && <div class="busy-dot" title={exporting ? progressLabel : undefined} />}
      {result && (
        <div class="viewer-tools">
          <button
            class="tool"
            title={t('viewer.zoomOut')}
            aria-label={t('viewer.zoomOut')}
            disabled={level === 0}
            onClick={() => setLevel((i) => Math.max(0, i - 1))}
          >
            −
          </button>
          <button
            class="tool readout"
            title={t('viewer.resetZoom')}
            aria-label={t('viewer.resetZoom')}
            onClick={() => {
              setLevel(nearestLevel(1));
              setPan({ x: 0, y: 0 });
            }}
          >
            {zoom >= 1 ? `${zoom}×` : zoom.toFixed(2)}
          </button>
          <button
            class="tool"
            title={t('viewer.zoomIn')}
            aria-label={t('viewer.zoomIn')}
            disabled={level === ZOOM_LEVELS.length - 1}
            onClick={() => setLevel((i) => Math.min(ZOOM_LEVELS.length - 1, i + 1))}
          >
            +
          </button>
          <button class="tool" title={t('viewer.fit')} aria-label={t('viewer.fit')} onClick={fit}>
            ⤢
          </button>
        </div>
      )}
      {result && stats && (
        <div class="statusbar">
          <span>
            {t('status.size')} <b>{stats.outW}×{stats.outH}</b>
          </span>
          {stats.clamped && (
            <span>
              {t('status.working')} <b>{stats.workW}×{stats.workH}</b>
            </span>
          )}
          <span>
            {t('status.time')} <b>{stats.ms.toFixed(1)}ms</b>
          </span>
          <span>
            {t('status.zoom')} <b>{zoom >= 1 ? zoom.toFixed(0) : zoom.toFixed(2)}×</b>
          </span>
          {stats.clamped ? (
            <span class="badge warn" title={t('status.clamped')}>
              {t('status.reduced')}
            </span>
          ) : (
            <span class="badge">{t('status.exact')}</span>
          )}
        </div>
      )}
      {!result && children}
      {peek && (
        <div class="statusbar" style={{ left: 'auto', right: '10px' }}>
          {t('viewer.original')}
        </div>
      )}
    </div>
  );
}
